// [claude-code 2026-03-20] S3:T5 — NotificationToast: bottom-left, theme colors, Don't Show Again
// [claude-code 2026-03-28] S8-T6: Refactored to RiskFlow card style — frosted glass, severity badges, bullish/bearish footer
import { X, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { useEffect, useState } from "react";
import { playAlertSound, playIOSPing, AlertType } from "../utils/soundAlerts";
import { useSettings } from "../contexts/SettingsContext";
import { useBackend } from "../lib/backend";
import { useToast, type NotificationType } from "../contexts/ToastContext";
import { useDND } from "../contexts/DNDContext";
import { healingBowlPlayer } from "../utils/healingBowlSounds";
import { isElectron } from "../lib/platform";
import type { AlertSeverity, RiskFlowAlert } from "../lib/riskflow-feed";
import { inferDirection } from "../lib/riskflow-feed";
import {
  alertSeverityToPalette,
  fuseScoreFromAlert,
} from "../lib/riskflow-card-utils";
import { isAgentTaskNotification } from "../lib/agent-task-notification-policy";
import { bucketOfAlert } from "../lib/source-buckets";
import { compactTimeAgo } from "../lib/time-utils";
import { riskFlowAlertScoringFromMetadata } from "../lib/riskflow-notification-metadata";
import { riskFlowVisualCandidates } from "../lib/riskflow-visual-enrichment";
import { extractYouTubeVideoId } from "../lib/youtube";
import type {
  NotificationCategory,
  NotificationPrefs,
  Severity,
} from "../lib/user-preferences";
import { RiskFlowCardAnatomy } from "./feed/RiskFlowCardAnatomy";
import { RiskFlowEventFuseGrid } from "./feed/RiskFlowEventFuseGrid";
import { NothingFuse } from "./shared/NothingFuse";
import "./notifications/s156-cardless-notifications.css";

export interface Notification {
  id: string;
  type: "iv" | "news" | "tilt" | "trade" | "warning" | "riskSignal";
  category: string;
  severity?: Severity | "info" | "warning" | "success" | "error";
  title: string;
  message: string;
  timestamp: Date;
  url?: string;
  eventId?: string;
  ivScore?: number | null;
  riskType?: RiskFlowAlert["riskType"];
  source?: RiskFlowAlert["source"];
  imageUrl?: string | null;
  videoUrl?: string | null;
  metadata?: Record<string, unknown>;
  exiting?: boolean;
}

/** Map backend notification type to DND notification type */
function toDndType(type: Notification["type"]): NotificationType {
  switch (type) {
    case "iv":
      return "iv-alert";
    case "news":
      return "news-alert";
    case "tilt":
      return "tilt-alert";
    case "trade":
      return "trade-alert";
    case "warning":
      return "general";
    case "riskSignal":
      return "risk-signal";
  }
}

const SEVERITY_ORDER: Severity[] = ["low", "medium", "high", "critical"];
const RISKFLOW_HEADLINE_CATEGORIES = new Set([
  "riskflow",
  "geopolitical_alerts",
  "econ_alerts",
]);
const RISK_SIGNAL_CATEGORIES = new Set(["risk_signals", "riskSignal"]);
const TOAST_EXIT_MS = 300;
const RISKFLOW_TOAST_BODY_CLASS = "pr-9";
const RISKFLOW_TOAST_BODY_MIN_HEIGHT_CLASS = "min-h-[92px]";
const RISKFLOW_TOAST_MIN_HEIGHT = "126px";

function isRiskFlowHeadlineCategory(category: string): boolean {
  return RISKFLOW_HEADLINE_CATEGORIES.has(category);
}

function isRiskSignalCategory(category: string): boolean {
  return RISK_SIGNAL_CATEGORIES.has(category);
}

function appendLatestNotifications(
  prev: Notification[],
  incoming: Notification[],
  limit = 5,
): Notification[] {
  const incomingIds = new Set(incoming.map((item) => item.id));
  return [
    ...incoming,
    ...prev.filter((item) => !incomingIds.has(item.id)),
  ].slice(0, limit);
}

function severityRank(severity?: Notification["severity"]): number {
  if (severity === "error") return SEVERITY_ORDER.indexOf("critical");
  if (severity === "warning") return SEVERITY_ORDER.indexOf("high");
  const rank = SEVERITY_ORDER.indexOf(severity as Severity);
  return rank >= 0 ? rank : SEVERITY_ORDER.indexOf("medium");
}

function categoryToToastType(category: string): Notification["type"] {
  if (category === "econ_alerts" || category === "geopolitical_alerts") {
    return "news";
  }
  if (category === "risk_signals" || category === "riskSignal") {
    return "riskSignal";
  }
  if (category === "riskflow" || category === "dailyBrief") return "iv";
  if (category === "toolApprovals" || category === "maintenance_request") {
    return "trade";
  }
  if (category === "system" || category === "test") return "warning";
  return "news";
}

function normalizeAlertSeverity(
  severity?: Notification["severity"],
): AlertSeverity {
  if (severity === "critical" || severity === "high") return severity;
  if (severity === "medium" || severity === "low") return severity;
  if (severity === "error") return "critical";
  if (severity === "warning") return "high";
  return "medium";
}

function severityScore(severity: AlertSeverity): number {
  if (severity === "critical") return 9;
  if (severity === "high") return 7;
  if (severity === "medium") return 5;
  return 3;
}

function riskTypeForCategory(category: string): RiskFlowAlert["riskType"] {
  if (category === "geopolitical_alerts") return "Geopolitical";
  if (category === "econ_alerts") return "Macro";
  return null;
}

function headlineFromNotification(notification: Notification): string {
  const message = notification.message.trim();
  if (message.length > 0) return message;
  return notification.title.trim() || "RiskFlow headline";
}

function sourceLabelFromNotification(notification: Notification): string {
  return notification.title
    .replace(/^Fintheon\s*·\s*/i, "")
    .replace(/^Fintheon\s*-\s*/i, "")
    .trim();
}

function notificationToRiskFlowAlert(
  notification: Notification,
): RiskFlowAlert {
  const severity = normalizeAlertSeverity(notification.severity);
  const publishedAt = notification.timestamp.toISOString();
  const source = notification.source ?? "backend";
  const scoring = riskFlowAlertScoringFromMetadata(notification.metadata);
  const riskType =
    notification.riskType ?? riskTypeForCategory(notification.category);

  return {
    id: notification.eventId ?? `notification-${notification.id}`,
    headline: headlineFromNotification(notification),
    summary: sourceLabelFromNotification(notification),
    url: notification.url,
    publishedAt,
    source,
    severity,
    tags: [notification.category],
    isBreaking: severity === "critical" || severity === "high",
    riskType,
    imageUrl: notification.imageUrl,
    videoUrl: notification.videoUrl,
    ivScore:
      notification.ivScore ??
      scoring.headlineIvScore ??
      severityScore(severity),
    direction: scoring.direction ?? null,
    ...scoring,
  };
}

function categoryLabel(category: string): string {
  switch (category) {
    case "geopolitical_alerts":
      return "Geo";
    case "econ_alerts":
      return "Econ";
    case "dailyBrief":
      return "Brief";
    case "chat_relay":
      return "Chat";
    case "maintenance_request":
      return "Maint";
    case "riskflow":
      return "RiskFlow";
    case "risk_signals":
    case "riskSignal":
      return "Risk Signal";
    default:
      return category.replace(/_/g, " ");
  }
}

function shouldShowOnThisSurface(
  notification: Notification,
  prefs: NotificationPrefs,
): boolean {
  const channelEnabled = isElectron()
    ? prefs.deliveryChannels.desktop
    : prefs.deliveryChannels.web;
  if (!channelEnabled) return false;
  const category = notification.category as NotificationCategory;
  if (notification.severity === "critical") return true;
  if (prefs.blockedCategories.includes(category)) return false;
  if (prefs.econOnlyMode && category !== "econ_alerts") return false;
  if (prefs.criticalOnly) return false;
  if (prefs.manualDnd) return false;
  return (
    severityRank(notification.severity) >= severityRank(prefs.severityThreshold)
  );
}

interface NotificationToastProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  onDismissAll?: () => void;
}

export function NotificationToast({
  notification,
  onDismiss,
  onDismissAll,
}: NotificationToastProps) {
  const { alertConfig } = useSettings();

  useEffect(() => {
    if (!alertConfig.soundEnabled) {
      const timer = setTimeout(() => {
        onDismiss(notification.id);
      }, 5000);
      return () => clearTimeout(timer);
    }

    if (
      notification.type === "tilt" ||
      notification.title.toLowerCase().includes("tilt") ||
      notification.title.toLowerCase().includes("fraction")
    ) {
      healingBowlPlayer.play(alertConfig.healingBowlSound);
    } else if (
      notification.type === "news" ||
      notification.type === "iv" ||
      notification.type === "riskSignal"
    ) {
      playIOSPing(alertConfig.soundEnabled);
    } else {
      let soundType: AlertType = "info";
      if (notification.severity === "warning") soundType = "warning";
      else if (notification.severity === "error") soundType = "error";
      else if (notification.severity === "success") soundType = "success";
      playAlertSound(soundType, alertConfig.soundEnabled);
    }

    const timer = setTimeout(() => {
      onDismiss(notification.id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [
    notification.id,
    notification.type,
    notification.severity,
    notification.title,
    onDismiss,
    alertConfig.soundEnabled,
    alertConfig.healingBowlSound,
  ]);

  const isVisible = !notification.exiting;
  const hiddenOffset = notification.type === "riskSignal" ? "16px" : "-16px";

  const MarkIcon =
    notification.severity === "error" ||
    notification.severity === "warning" ||
    notification.type === "tilt" ||
    notification.type === "riskSignal"
      ? AlertTriangle
      : notification.type === "trade"
        ? CheckCircle2
        : Info;

  // Determine bullish/bearish from content heuristics
  const isBullish = /bull|long|buy|up|rally|bid/i.test(
    notification.title + notification.message,
  );
  const isBearish = /bear|short|sell|down|drop|crash|risk|tilt/i.test(
    notification.title + notification.message,
  );
  const dirColor = isBullish
    ? "var(--fintheon-bullish)"
    : isBearish
      ? "var(--fintheon-bearish)"
      : "var(--fintheon-muted)";

  return (
    <div
      className="transition-all duration-300 ease-out overflow-hidden group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateX(0)" : `translateX(${hiddenOffset})`,
        transition: isVisible
          ? "opacity 220ms var(--ease-spring), transform 260ms var(--ease-spring)"
          : "opacity 300ms ease-out, transform 300ms ease-out",
        minWidth: "280px",
        maxWidth: "380px",
        pointerEvents: isVisible ? "auto" : "none",
      }}
    >
      <div
        className="s156-notification-row"
        data-severity={notification.severity ?? notification.type}
      >
        <div
          className="flex items-start justify-between"
          style={{ padding: "10px 12px" }}
        >
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <span className="fintheon-toast-mark mt-0.5">
              <MarkIcon aria-hidden="true" size={13} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] leading-snug font-medium text-[var(--fintheon-text)] line-clamp-2">
                {notification.title}
              </p>
              {notification.message &&
                notification.message !== notification.title && (
                  <p className="text-[10px] text-[var(--fintheon-muted)] line-clamp-1 mt-0.5">
                    {notification.message}
                  </p>
                )}
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[10px] text-[var(--fintheon-muted)]">
                  {notification.timestamp.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="text-[10px] text-[var(--fintheon-muted)]/55">
                  &middot;
                </span>
                <span
                  className="text-[10px] tracking-wider uppercase"
                  style={{
                    color:
                      "color-mix(in srgb, var(--fintheon-accent) 60%, transparent)",
                  }}
                >
                  {notification.type === "iv"
                    ? "IV"
                    : notification.type === "tilt"
                      ? "Psych"
                      : notification.type === "trade"
                        ? "Trade"
                        : categoryLabel(notification.category)}
                </span>
              </div>
            </div>
          </div>
          <div
            className="fintheon-toast-hover-actions flex-shrink-0 flex items-center gap-0"
            style={{ marginLeft: "4px" }}
          >
            {onDismissAll ? (
              <button
                type="button"
                onClick={onDismissAll}
                className="fintheon-toast-action-button fintheon-toast-action-button--secondary min-h-5 px-1.5 text-[10px]"
                aria-label="Close all notifications"
                title="Close all notifications"
              >
                Close All
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onDismiss(notification.id)}
              aria-label="Dismiss notification"
              title="Dismiss notification"
              className="s156-notification-icon-control"
              style={{ color: "var(--fintheon-muted)" }}
            >
              <X aria-hidden="true" size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RiskFlowNotificationCard({
  notification,
  onDismiss,
  onDismissAll,
  autoDismissMs = 12000,
}: {
  notification: Notification;
  onDismiss: (id: string) => void;
  onDismissAll?: () => void;
  autoDismissMs?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const alert = notificationToRiskFlowAlert(notification);
  const direction = alert.direction ?? inferDirection(alert);
  const fuseScore = fuseScoreFromAlert(alert);
  const sourceLabel =
    sourceLabelFromNotification(notification) || bucketOfAlert(alert);
  const severity = alertSeverityToPalette(alert.severity);
  const visual =
    riskFlowVisualCandidates(alert).find(
      (candidate) => candidate.kind === "image" && Boolean(candidate.imageUrl),
    ) ?? null;
  const playableVideoUrl =
    alert.videoUrl && !extractYouTubeVideoId(alert.videoUrl)
      ? alert.videoUrl
      : null;
  const hasExpandedMedia = Boolean(playableVideoUrl || visual);

  useEffect(() => {
    if (autoDismissMs <= 0) return;
    const timer = setTimeout(() => onDismiss(notification.id), autoDismissMs);
    return () => clearTimeout(timer);
  }, [autoDismissMs, notification.id, onDismiss]);

  const toggleExpanded = () => setExpanded((value) => !value);
  const isVisible = !notification.exiting;

  return (
    <div
      className={`fintheon-toast-surface solvys-liquid-glass fintheon-black-liquid-glass notification-stack-liquid-glass riskflow-notification-toast group overflow-hidden rounded-lg border ${
        expanded ? "riskflow-notification-toast--expanded" : ""
      }`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${expanded ? "Collapse" : "Expand"} RiskFlow notification: ${alert.headline}`}
      title={expanded ? "Collapse RiskFlow details" : "Expand RiskFlow details"}
      onClick={toggleExpanded}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggleExpanded();
        }
      }}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateX(0)" : "translateX(16px)",
        transition: isVisible
          ? [
              "opacity 240ms var(--ease-spring)",
              "transform 420ms cubic-bezier(0.22, 1, 0.36, 1)",
              "width 520ms cubic-bezier(0.22, 1, 0.36, 1)",
              "min-height 560ms cubic-bezier(0.22, 1, 0.36, 1)",
            ].join(", ")
          : "opacity 300ms ease-out, transform 300ms ease-out",
        width: expanded ? "620px" : "380px",
        maxWidth: "calc(100vw - 32px)",
        minHeight: expanded ? "315px" : RISKFLOW_TOAST_MIN_HEIGHT,
        transformOrigin: "bottom right",
        cursor: "pointer",
        pointerEvents: isVisible ? "auto" : "none",
      }}
    >
      <div className="relative">
        <RiskFlowCardAnatomy
          title={alert.headline}
          sourceLabel={sourceLabel}
          timestampLabel={compactTimeAgo(alert.publishedAt)}
          severity={alertSeverityToPalette(alert.severity)}
          fuseScore={fuseScore}
          ivScore={alert.ivScore}
          direction={direction}
          directionalInput={alert}
          compact
          fresh
          freshSurface={false}
          showVerticalFuse={false}
          bodyClassName={RISKFLOW_TOAST_BODY_CLASS}
          bodyMinHeightClass={RISKFLOW_TOAST_BODY_MIN_HEIGHT_CLASS}
          className="s156-riskflow-notification-anatomy border-0"
        />
        <div
          className="fintheon-toast-hover-actions absolute right-2 top-2 flex items-center gap-0"
          style={{ zIndex: 2 }}
        >
          {onDismissAll ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDismissAll();
              }}
              className="fintheon-toast-action-button fintheon-toast-action-button--secondary min-h-5 px-1.5 text-[10px]"
              aria-label="Close all RiskFlow notifications"
              title="Close all RiskFlow notifications"
            >
              Close All
            </button>
          ) : null}
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDismiss(notification.id);
            }}
            aria-label="Dismiss RiskFlow notification"
            title="Dismiss RiskFlow notification"
            className="s156-notification-icon-control flex items-center justify-center"
          >
            <X aria-hidden="true" size={11} />
          </button>
        </div>
      </div>
      <div
        className={`riskflow-toast-expanded-panel grid gap-2 px-2 ${
          hasExpandedMedia ? "grid-cols-2" : "grid-cols-1"
        }`}
        data-open={expanded ? "true" : "false"}
        aria-hidden={!expanded}
      >
        {expanded && (
          <>
            {playableVideoUrl ? (
              <video
                src={playableVideoUrl}
                poster={alert.imageUrl ?? undefined}
                muted
                loop
                autoPlay
                playsInline
                className="h-28 w-full object-cover"
                onClick={(event) => event.stopPropagation()}
              />
            ) : visual ? (
              <a
                href={visual.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                className="block overflow-hidden"
              >
                <img
                  src={visual.imageUrl}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="h-28 w-full object-cover"
                />
              </a>
            ) : null}
            <RiskFlowEventFuseGrid
              alert={alert}
              severity={severity}
              className="min-h-28 !border-0 bg-transparent p-2.5"
            />
          </>
        )}
      </div>
      <div
        className={`riskflow-notification-fuse-footer px-2 pb-2 ${
          isVisible ? "riskflow-notification-fuse-active" : ""
        }`}
      >
        <NothingFuse
          value={Math.min(1, Math.max(0.15, fuseScore / 10))}
          severity={severity}
          thickness={4}
          animateIn
        />
      </div>
    </div>
  );
}

function isCriticalNotification(n: Notification): boolean {
  // Margin call, system errors, and backend-down always break through
  if (
    n.severity === "error" &&
    (n.title.toLowerCase().includes("margin") ||
      n.title.toLowerCase().includes("system"))
  )
    return true;
  if (n.type === "warning" && n.severity === "error") return true;
  return false;
}

export function NotificationContainer() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [riskSignalNotifications, setRiskSignalNotifications] = useState<
    Notification[]
  >([]);
  const [lastChecked, setLastChecked] = useState<Date>(new Date());
  const backend = useBackend();
  const { preferences } = useSettings();
  const { isNotificationBlocked } = useToast();
  const { dndActive, tryQueue } = useDND();

  const dismissNotification = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, exiting: true } : n)),
    );
    setRiskSignalNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, exiting: true } : n)),
    );
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setRiskSignalNotifications((prev) => prev.filter((n) => n.id !== id));
    }, TOAST_EXIT_MS);
  };
  const dismissAllNotifications = (lane: "system" | "signal") => {
    const setItems =
      lane === "signal" ? setRiskSignalNotifications : setNotifications;
    setItems((items) => items.map((item) => ({ ...item, exiting: true })));
    setTimeout(() => setItems([]), TOAST_EXIT_MS);
  };

  useEffect(() => {
    let isMounted = true;

    const checkNotifications = async () => {
      try {
        const result = await backend.notifications.list();

        if (!isMounted) return;

        const notifications = Array.isArray(result) ? result : [];
        const newNotifications = notifications
          .filter(
            (item: any) => new Date(item.createdAt) > lastChecked && !item.read,
          )
          .map((item: any) => ({
            id: item.id.toString(),
            type: categoryToToastType(
              String(item.category ?? item.type ?? "system"),
            ),
            category: String(item.category ?? item.type ?? "system"),
            severity: item.severity as any,
            title: item.title,
            message: item.body ?? item.message ?? "",
            timestamp: new Date(item.createdAt),
            url: typeof item.url === "string" ? item.url : undefined,
            eventId:
              typeof item.eventId === "string"
                ? item.eventId
                : typeof item.itemId === "string"
                  ? item.itemId
                  : undefined,
            metadata:
              item.metadata && typeof item.metadata === "object"
                ? (item.metadata as Record<string, unknown>)
                : undefined,
            ivScore:
              typeof item.ivScore === "number"
                ? item.ivScore
                : typeof item.score === "number"
                  ? item.score
                  : null,
            riskType: item.riskType ?? null,
            source: item.source,
          }))
          .filter((n: Notification) => !isAgentTaskNotification(n))
          .filter((n: Notification) => !isRiskFlowHeadlineCategory(n.category))
          .filter((n: Notification) =>
            shouldShowOnThisSurface(n, preferences.notifications),
          )
          // Filter out blocked notification types
          .filter(
            (n: Notification) => !isNotificationBlocked(toDndType(n.type)),
          );

        if (newNotifications.length > 0) {
          // When DND is active, queue non-critical notifications
          const toShow: Notification[] = [];
          for (const n of newNotifications) {
            const critical = isCriticalNotification(n);
            const queued = tryQueue(
              {
                id: n.id,
                type: n.type,
                title: n.title,
                message: n.message,
                timestamp: n.timestamp,
                severity: n.severity,
              },
              critical ? undefined : "non-critical",
            );
            if (!queued) {
              // Not queued — show immediately (DND off, or critical alert)
              toShow.push(n);
            }
          }
          if (toShow.length > 0) {
            const riskSignalsToShow = toShow.filter((n) =>
              isRiskSignalCategory(n.category),
            );
            const toastToShow = toShow.filter(
              (n) =>
                !isRiskFlowHeadlineCategory(n.category) &&
                !isRiskSignalCategory(n.category),
            );

            if (toastToShow.length > 0) {
              setNotifications((prev) => [...prev, ...toastToShow]);
            }
            if (riskSignalsToShow.length > 0) {
              setRiskSignalNotifications((prev) =>
                appendLatestNotifications(prev, riskSignalsToShow),
              );
            }
          }
          setLastChecked(new Date());
        }
      } catch (error) {
        console.warn("Failed to fetch notifications:", error);
      }
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 10000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [
    lastChecked,
    backend,
    isNotificationBlocked,
    dndActive,
    tryQueue,
    preferences.notifications,
  ]);

  return (
    <>
      {notifications.length > 0 && (
        <div
          className="fintheon-toast-lane fixed z-50 flex flex-col gap-1"
          data-stacked={notifications.length > 1 ? "true" : "false"}
          style={{ bottom: "56px", left: "56px" }}
        >
          {notifications.slice(0, 5).map((notification, index) => (
            <NotificationToast
              key={notification.id}
              notification={notification}
              onDismiss={dismissNotification}
              onDismissAll={
                notifications.length > 1 && index === 0
                  ? () => dismissAllNotifications("system")
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {riskSignalNotifications.length > 0 && (
        <div
          className="fintheon-toast-lane fixed z-50 flex flex-col items-end gap-1"
          data-stacked={riskSignalNotifications.length > 1 ? "true" : "false"}
          style={{ top: "58px", right: "18px" }}
        >
          {riskSignalNotifications.slice(0, 5).map((notification, index) => (
            <NotificationToast
              key={notification.id}
              notification={notification}
              onDismiss={dismissNotification}
              onDismissAll={
                riskSignalNotifications.length > 1 && index === 0
                  ? () => dismissAllNotifications("signal")
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </>
  );
}
